import type { CommonScenario, CompareVehicleRef } from 'fuel-benchmark-contracts';
import { cn } from '../../lib/cn';
import { formatDecimal } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { scenarioSummary } from './compare-display';

export interface ScenarioCardsProps {
  scenarios: readonly CommonScenario[];
  vehicles: readonly CompareVehicleRef[];
  selectedScenarioId: string;
  onSelect: (scenarioId: string) => void;
}

/**
 * Step B (plan §7): only the scenarios every picked vehicle has already proven, each card carrying how many
 * valid runs back it and what each vehicle averaged — so the choice is made on evidence, not on a code.
 */
export const ScenarioCards = ({ scenarios, vehicles, selectedScenarioId, onSelect }: ScenarioCardsProps) => {
  const { t } = useI18nTranslation();

  if (scenarios.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-8">
        <p className="text-sm text-muted-foreground">{t('compare.scenarios.empty')}</p>
      </div>
    );
  }

  const labelOf = (vehicleId: string): string => vehicles.find((vehicle) => vehicle.id === vehicleId)?.code ?? vehicleId;

  return (
    <div
      role="radiogroup"
      aria-label={t('compare.scenarios.title')}
      className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
    >
      {scenarios.map((entry) => {
        const isSelected = entry.scenario.id === selectedScenarioId;

        return (
          <button
            key={entry.scenario.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(entry.scenario.id)}
            className={cn(
              'rounded-lg border p-4 text-start transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              isSelected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted',
            )}
          >
            <p className="truncate font-mono text-xs text-muted-foreground">{entry.scenario.code}</p>
            <p className="mt-1 truncate text-sm font-medium text-card-foreground">{scenarioSummary(entry.scenario, t)}</p>

            <dl className="mt-3 space-y-1 border-t border-border pt-2">
              {entry.perVehicle.map((perVehicle) => (
                <div
                  key={perVehicle.vehicleId}
                  className="flex items-baseline justify-between gap-2 text-xs"
                >
                  <dt className="truncate font-mono text-muted-foreground">{labelOf(perVehicle.vehicleId)}</dt>
                  <dd className="whitespace-nowrap text-foreground">
                    {perVehicle.meanResult === null ? t('common.notSet') : `${formatDecimal(perVehicle.meanResult, 1)} ${perVehicle.unit}`}
                    <span className="text-muted-foreground">
                      {' · '}
                      {perVehicle.validRunCount === 1
                        ? t('compare.result.runsOne')
                        : t('compare.result.runsMany', { count: perVehicle.validRunCount })}
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
          </button>
        );
      })}
    </div>
  );
};
